import {
  Home,
  AlertTriangle,
  Sparkles,
  BarChart3,
  Route as RouteIcon,
  FolderHeart,
  Send,
  Clock3,
  MailX,
  FileQuestion,
  Search,
  Repeat2,
  FileText,
  TrendingUp,
  Timer,
  Smartphone,
  Users,
  Share2,
  Bell,
  Puzzle,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { PageHero } from "@/components/blocks/PageHero";
import { SectionHeading } from "@/components/blocks/SectionHeading";
import { CTABand } from "@/components/blocks/CTABand";
import { HeroCard } from "@/components/blocks/HeroCard";
import { ExistingSponsorship } from "@/components/blocks/ExistingSponsorship";
import { Steps } from "@/components/blocks/Steps";
import { Reveal } from "@/components/Reveal";
import { CLUB_FEATURES } from "@/lib/features";
import { ROUTE_DEMO, waitlistFor } from "@/lib/brand";
import { CUSTOM_DELIVERABLES, CUSTOM_DELIVERABLES_TITLE } from "@/lib/measurement";

// Vereine werden vor allem gefunden - Funktionen wie die Filtersuche stehen
// deshalb nur kompakt am Ende des Rasters statt als volle Karte.
const PRIMARY = CLUB_FEATURES.filter((f) => !f.secondaryFor?.includes("club"));
const SECONDARY = CLUB_FEATURES.filter((f) => f.secondaryFor?.includes("club"));

const SIDEBAR_ITEMS = [
  { id: "top", label: "Start", icon: Home },
  { id: "herausforderung", label: "Was euch ausbremst", icon: AlertTriangle },
  { id: "funktionen", label: "Was ihr bekommt", icon: Sparkles },
  { id: "analytics", label: "Sponsoren halten", icon: BarChart3 },
  { id: "aufwand", label: "Wenig Aufwand", icon: Timer },
  { id: "projekte", label: "Projekte", icon: FolderHeart },
  { id: "ablauf", label: "So läuft es ab", icon: RouteIcon },
  { id: "cta", label: "Loslegen", icon: Send },
];

const PAINS = [
  {
    icon: Clock3,
    title: "Sponsorensuche frisst Abende",
    text: "Listen wälzen, Telefonnummern recherchieren, Klinken putzen - meist neben Beruf, Training und Spielbetrieb.",
  },
  {
    icon: MailX,
    title: "Anfragen verlaufen im Sand",
    text: "Die schöne PDF-Mappe landet im Sammelpostfach der falschen Person und wird nie wieder erwähnt.",
  },
  {
    icon: FileQuestion,
    title: "Niemand weiß, was es gebracht hat",
    text: "Nach der Saison fehlt der Nachweis über die Wirkung - und damit das Argument für die Verlängerung.",
  },
];

const RETENTION = [
  {
    icon: Repeat2,
    title: "Verlängerung statt Neuanfang",
    text: "Ein Sponsor, der schwarz auf weiß sieht, was seine Unterstützung gebracht hat, verlängert eher, als dass ihr im nächsten Sommer wieder bei null anfangt.",
  },
  {
    icon: FileText,
    title: "Ein Bericht statt einer Bitte",
    text: "Statt erneut zu fragen, legt ihr die Auswertung vor. Das verändert das Gespräch grundlegend - ihr liefert eine Bilanz, keine Bittstellung.",
  },
  {
    icon: TrendingUp,
    title: "Argumente für mehr",
    text: "Wenn die Zahlen stimmen, lässt sich über einen größeren Umfang reden. Ohne Zahlen bleibt jedes Gespräch beim Betrag des Vorjahres.",
  },
];

const STEPS = [
  {
    no: "01",
    title: "Profil anlegen",
    text: "Sportarten, Reichweite, Zielgruppe und verfügbare Flächen eintragen.",
  },
  {
    no: "02",
    title: "Projekt ausschreiben",
    text: "Ein konkretes Vorhaben mit Ziel, Budget und Gegenleistung.",
    optional: true,
  },
  {
    no: "03",
    title: "Suchen und gefunden werden",
    text: "Filtersuche nutzen oder auf Vorschläge mit MatchScore warten.",
  },
  {
    no: "04",
    title: "Ins Gespräch kommen",
    text: "Erstkontakt über den Chat, Unterlagen aus dem Pitch-Generator dazu.",
  },
  {
    no: "05",
    title: "Sponsoring einrichten",
    text: "Gemeinsam mit dem Partner festhalten, was vereinbart ist. Beide bestätigen.",
  },
  {
    no: "06",
    title: "Wirkung belegen",
    text: "Am Spieltag in Sekunden eintragen, der Rest läuft automatisch. Der Bericht überzeugt beim nächsten Mal.",
  },
];

/** Rechte Spalte im Kopfbereich. */
const AT_A_GLANCE = [
  {
    icon: Search,
    title: "Gefunden werden",
    text: "Ein Profil, über das passende Unternehmen euch finden, statt Klinken zu putzen.",
  },
  {
    icon: FolderHeart,
    title: "Gezielt fragen",
    text: "Projekte mit Ziel, Budget und Gegenleistung ausschreiben, wenn ihr wollt.",
  },
  {
    icon: Timer,
    title: "Wenig Aufwand",
    text: "Am Spieltag in Sekunden eingetragen, vieles läuft automatisch.",
  },
  {
    icon: Repeat2,
    title: "Sponsoren halten",
    text: "Ein Bericht, der für die Verlängerung spricht.",
  },
];

/** Warum das Belegen für Vereine kaum Arbeit macht. */
const EFFORT = [
  {
    icon: Smartphone,
    title: "Ein Link nach dem Spiel",
    text: "Nach jedem Heimspiel kommt ein Link aufs Handy. Zuschauer antippen, Durchsage abhaken, fertig. Ohne Anmeldung.",
  },
  {
    icon: Users,
    title: "Helfer ohne eigenes Konto",
    text: "Kasse, Platzwart oder Jugendtrainer tragen über einen eigenen Link ein. Jeder Link lässt sich jederzeit widerrufen.",
  },
  {
    icon: Share2,
    title: "Beiträge werden erkannt",
    text: "Markiert ihr euren Partner auf Instagram, zählt MatchGate den Beitrag samt Aufrufen automatisch mit.",
  },
  {
    icon: Bell,
    title: "Erinnerungen statt Listen",
    text: "Was fällig ist, meldet MatchGate von selbst: die Durchsage am Spieltag oder das Foto der Bande zur Halbserie.",
  },
];

// Ein Projekt übernimmt vorerst ein Unternehmen allein, nicht mehrere
// anteilig. Deshalb zeigen die Beispiele Budget, Gegenleistung und Laufzeit
// statt eines Finanzierungsstands.
const PROJECTS = [
  {
    club: "Fußball · Kreisliga",
    title: "Trikotsatz für die A-Jugend",
    text: "22 Sätze inklusive Aufwärmshirts, sichtbar in 30 Saisonspielen und auf allen Vereinskanälen.",
    budget: "4.800 €",
    giveBack: "Logo auf Trikot und Aufwärmshirt",
    duration: "2 Saisons",
  },
  {
    club: "Handball · Jugend",
    title: "Sommercamp für 60 Kinder",
    text: "Eine Woche Training, Verpflegung und Betreuung, inklusive Namensnennung im Camp-Titel.",
    budget: "9.500 €",
    giveBack: "Name im Camp-Titel",
    duration: "Sommer 2027",
  },
  {
    club: "Leichtathletik",
    title: "Neue Zeitmessanlage",
    text: "Digitale Messung für Wettkämpfe der gesamten Region, Bandenfläche direkt an der Bahn.",
    budget: "14.000 €",
    giveBack: "Bande direkt an der Bahn",
    duration: "5 Jahre",
  },
  {
    club: "Rudern",
    title: "Sanierung Bootshaus",
    text: "Dach und Fassade des Vereinsheims, dauerhaft sichtbar am Wasser und bei jeder Regatta.",
    budget: "26.000 €",
    giveBack: "Logo an der Fassade",
    duration: "10 Jahre",
  },
];

export default function ForClubs() {
  return (
    <PageLayout
      sidebarItems={SIDEBAR_ITEMS}
      title="Für Vereine"
      testId="for-clubs-main"
    >
      <PageHero
        eyebrow="Für Vereine"
        titleLines={["Euer Verein hat", "mehr verdient als"]}
        titleAccent="ein Nein per Mail."
        lead="MatchGate macht aus der Sponsorensuche eine Struktur: ein Profil, das gefunden wird, Projekte, die ihr ausschreiben könnt, ein MatchScore, der zeigt, wie gut ihr zusammenpasst - und eine Auswertung, die am Saisonende für euch spricht."
        primary={{ label: "Platz vormerken", to: waitlistFor("verein") }}
        secondary={{ label: "Was ihr bekommt", href: "#funktionen" }}
        chipsLabel="Für Vereine mit dabei"
        chips={CLUB_FEATURES.map((f) => f.title)}
      >
        <HeroCard kicker="Für euren Verein auf einen Blick" items={AT_A_GLANCE} />
      </PageHero>

      {/* Ausgangslage */}
      <section
        id="herausforderung"
        data-testid="clubs-pains-section"
        className="relative py-24 lg:py-32"
      >
        <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-sticky">
              <SectionHeading
                eyebrow="Was euch ausbremst"
                title="Sponsoring scheitert selten am "
                accent="Willen"
                tail="."
                lead="Es scheitert an Zeit, an fehlenden Kontakten und daran, dass niemand belegen kann, was ein Engagement gebracht hat. Genau diese drei Punkte nehmen wir euch ab."
                className="max-w-md mb-0"
              />
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col gap-5">
            {PAINS.map((pain, i) => {
              const Icon = pain.icon;
              return (
                <Reveal key={pain.title} delay={i * 0.06}>
                  <div className="group flex gap-6 rounded-3xl border border-white/10 backdrop-blur-xl p-7 md:p-8 hover:border-white/20 transition-colors duration-500">
                    <span className="flex items-center justify-center w-12 h-12 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                      <Icon className="w-5 h-5 text-brand-orange" />
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-xl md:text-2xl text-white mb-2">
                        {pain.title}
                      </h3>
                      <p className="text-base leading-relaxed text-white/55">
                        {pain.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Funktionen */}
      <section
        id="funktionen"
        data-testid="clubs-features-section"
        className="relative py-24 lg:py-32 border-y border-white/[0.06]"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-1/4 -left-40 w-[600px] h-[500px] rounded-full bg-brand-orange/10 blur-[160px]" />
        </div>

        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Was ihr bekommt"
            title="Acht Funktionen - alle im "
            accent="Abo enthalten"
            tail="."
            lead="Es gibt keine Stufen und keine Zusatzpakete. Was hier steht, könnt ihr ab dem ersten Tag nutzen."
          />

          <div className="grid gap-5 md:gap-6 lg:grid-cols-2">
            {PRIMARY.map((f, i) => {
              const Icon = f.icon;
              const no = String(i + 1).padStart(2, "0");
              return (
                <Reveal key={f.id} delay={(i % 2) * 0.05}>
                  <div className="group relative h-full overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-8 hover:border-white/20 transition-colors duration-500">
                    <div className="absolute -right-4 -top-8 font-display font-black text-[7rem] leading-none text-white/[0.03] group-hover:text-white/[0.06] transition-colors duration-500 select-none">
                      {no}
                    </div>

                    <div className="relative">
                      <div className="flex items-center gap-3 mb-5">
                        <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10">
                          <Icon className="w-5 h-5 text-brand-blue" />
                        </span>
                        <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.15em] font-bold text-brand-blue">
                          {f.title}
                        </span>
                      </div>

                      <h3 className="font-display font-bold text-2xl text-white mb-3">
                        {f.club.headline}
                      </h3>
                      <p className="text-base leading-relaxed text-white/55">
                        {f.club.text}
                      </p>

                      <ul className="mt-6 pt-6 border-t border-white/10 space-y-2.5">
                        {f.club.points.map((p) => (
                          <li key={p} className="flex items-center gap-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-brand-orange to-brand-blue shrink-0" />
                            <span className="text-sm text-white/70">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-brand-orange to-brand-blue group-hover:w-full transition-all duration-700" />
                  </div>
                </Reveal>
              );
            })}

            {SECONDARY.map((f) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.id} delay={0.05}>
                  <div className="h-full flex items-center gap-5 rounded-3xl border border-white/10 border-dashed backdrop-blur-xl px-8 py-7">
                    <span className="flex items-center justify-center w-11 h-11 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                      <Icon className="w-5 h-5 text-white/50" />
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-lg text-white">
                        {f.title}
                      </h3>
                      <p className="mt-1 text-sm leading-relaxed text-white/50">
                        {f.club.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Analytics / Sponsoren halten */}
      <section
        id="analytics"
        data-testid="clubs-analytics-section"
        className="relative py-24 lg:py-32"
      >
        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Sponsoren halten"
            title="Der beste Sponsor ist der, der "
            accent="bleibt"
            tail="."
            lead="Das Analytics-Dashboard richtet sich vor allem an das Unternehmen - und genau deshalb ist es für euch so wertvoll. Wer sieht, dass sein Geld gewirkt hat, bleibt."
          />

          <div className="grid gap-5 md:gap-6 lg:grid-cols-3">
            {RETENTION.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal key={r.title} delay={i * 0.07}>
                  <div className="group relative h-full overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-8 hover:border-white/20 transition-colors duration-500">
                    <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 mb-6">
                      <Icon className="w-5 h-5 text-brand-orange" />
                    </span>
                    <h3 className="font-display font-bold text-2xl text-white mb-3">
                      {r.title}
                    </h3>
                    <p className="text-base leading-relaxed text-white/55">
                      {r.text}
                    </p>
                    <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-brand-orange to-brand-blue group-hover:w-full transition-all duration-700" />
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-6 rounded-3xl border border-white/10 backdrop-blur-xl px-8 py-7 flex flex-col sm:flex-row sm:items-center gap-5">
              <span className="flex items-center justify-center w-12 h-12 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                <BarChart3 className="w-5 h-5 text-brand-blue" />
              </span>
              <p className="text-base leading-relaxed text-white/60">
                Euer Partner sieht im Dashboard{" "}
                <span className="text-white/85">
                  schwarz auf weiß, was ihr für ihn getan habt
                </span>
                . Ihr seht dasselbe, Zahl für Zahl mit Herkunft.
              </p>
            </div>
          </Reveal>

          <ExistingSponsorship
            className="mt-6"
            title="Bringt eure bestehenden Sponsoren mit."
            text="Ihr habt schon Partner? Holt sie mit auf MatchGate. Ihr richtet das Sponsoring gemeinsam ein, ab dann läuft die Auswertung mit, auch mitten in der Saison. Was vorher schon geliefert wurde, tragt ihr nach. Für Sponsorings, die schon vor MatchGate bestanden, fällt keine Provision an. Wie immer braucht jede Seite dafür ihr Abo."
          />
        </div>
      </section>

      {/* Wenig Aufwand */}
      <section
        id="aufwand"
        data-testid="clubs-effort-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-1/4 -left-32 w-[560px] h-[440px] rounded-full bg-brand-orange/10 blur-[170px]" />
        </div>

        <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-sticky">
              <SectionHeading
                eyebrow="Wenig Aufwand"
                title="Eintragen kostet euch Sekunden, "
                accent="nicht Abende"
                tail="."
                lead="Ihr macht nichts doppelt. Was sich automatisch erfassen lässt, läuft automatisch. Was nur ihr wisst, tragt ihr nach dem Spiel in wenigen Sekunden ein, auch vom Handy am Spielfeldrand."
                className="max-w-md mb-8"
              />
              <Reveal delay={0.1}>
                <Link
                  to={ROUTE_DEMO}
                  className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white/85 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] hover:text-white transition-all duration-300"
                >
                  Erfassung in der Demo ausprobieren
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                </Link>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid gap-5 sm:grid-cols-2">
              {EFFORT.map((e, i) => {
                const Icon = e.icon;
                return (
                  <Reveal key={e.title} delay={i * 0.06}>
                    <div className="h-full rounded-3xl border border-white/10 backdrop-blur-xl p-7 hover:border-white/20 transition-colors duration-500">
                      <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 mb-5">
                        <Icon className="w-5 h-5 text-brand-orange" />
                      </span>
                      <h3 className="font-display font-bold text-xl text-white mb-2">
                        {e.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-white/55">
                        {e.text}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={0.1}>
              <div className="mt-5 flex flex-col sm:flex-row gap-5 rounded-3xl border border-white/10 backdrop-blur-xl p-7">
                <span className="flex items-center justify-center w-11 h-11 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                  <Puzzle className="w-5 h-5 text-brand-blue" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-xl text-white mb-2">
                    {CUSTOM_DELIVERABLES_TITLE}
                  </h3>
                  <p className="text-sm leading-relaxed text-white/55">
                    {CUSTOM_DELIVERABLES}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Projekte */}
      <section
        id="projekte"
        data-testid="clubs-projects-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute bottom-0 right-0 w-[600px] h-[420px] rounded-full bg-brand-blue/10 blur-[160px]" />
        </div>

        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Projekte"
            title="Freiwillig - aber oft der "
            accent="schnellere Weg"
            tail="."
            lead="Ihr müsst keine Projekte anlegen; ein allgemeines Sponsoring reicht völlig. Wer ein konkretes Vorhaben ausschreibt, gibt Unternehmen aber etwas Greifbares an die Hand. Vier Beispiele, wie das aussehen kann."
          />

          <div className="grid gap-5 md:gap-6 sm:grid-cols-2">
            {PROJECTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="group relative h-full flex flex-col rounded-3xl border border-white/10 backdrop-blur-xl p-8 hover:border-white/20 transition-colors duration-500">
                  <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/35">
                    {p.club}
                  </span>
                  <h3 className="mt-3 font-display font-bold text-2xl text-white">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-white/55">
                    {p.text}
                  </p>

                  <dl className="mt-auto pt-7">
                    {/* Gegenleistung in voller Breite: Wort und Werte sind für ein Drittel zu lang. */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-5 border-t border-white/10 pt-6">
                      {[
                        ["Budget", p.budget],
                        ["Laufzeit", p.duration],
                        ["Gegenleistung", p.giveBack, true],
                      ].map(([label, value, wide]) => (
                        <div key={label} className={wide ? "col-span-2" : ""}>
                          <dt className="text-[10px] uppercase tracking-[0.15em] font-bold text-white/35">
                            {label}
                          </dt>
                          <dd className="mt-1.5 text-sm font-semibold leading-snug text-white">
                            {value}
                          </dd>
                        </div>
                      ))}
                    </div>
                  </dl>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-8 text-xs text-white/30">
              Beispielprojekte zur Veranschaulichung der Darstellung.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Ablauf */}
      <section
        id="ablauf"
        data-testid="clubs-steps-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="So läuft es ab"
            title="Von der Anmeldung bis zum "
            accent="Bericht"
            tail="."
          />

          <Steps steps={STEPS} />
        </div>
      </section>

      <CTABand
        title="Bringt euren Verein durch das "
        accent="Gate"
        tail="."
        lead="Profil anlegen, gefunden werden, Wirkung belegen. Alle acht Funktionen sind ab dem ersten Tag dabei."
        primary={{ label: "Platz vormerken", to: waitlistFor("verein") }}
        secondary={{ label: "Was es kostet", to: "/preisstruktur" }}
      />
    </PageLayout>
  );
}
